import { useMemo, useState } from 'react'
import { Check, ChevronDown, Search, User2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS,
} from '@/lib/menu-chrome'
import type { AssigneeFacet, GroupedAssigneeNames } from '@/lib/basecamp-assignee'
import type { TodosListMode } from '@/lib/basecamp-view-persist'
import { testIds } from '@/lib/test-ids'

function PersonRow({
  name,
  selected,
  onSelect,
}: {
  name: string
  selected: boolean
  onSelect: () => void
}) {
  return (
    <DropdownMenuItem className="gap-2 text-xs" onSelect={onSelect}>
      <Check className={cn('h-3.5 w-3.5', selected ? 'opacity-100' : 'opacity-0')} />
      {name}
    </DropdownMenuItem>
  )
}

const ORG_KINDS: { id: AssigneeFacet; label: string }[] = [
  { id: 'all', label: 'Everyone' },
  { id: 'me', label: 'Me' },
  { id: 'hop', label: 'Hop Studios' },
  { id: 'clients', label: 'Clients' },
  { id: 'unassigned', label: 'Unassigned' },
]

/** Me lens is already assigned-to-you. Hide Me/Hop/Client so they do not look like a second mode. */
const ME_KINDS: { id: AssigneeFacet; label: string }[] = [
  { id: 'all', label: 'Everyone' },
  { id: 'unassigned', label: 'Unassigned' },
]

function filterGroup(names: string[], needle: string): string[] {
  if (!needle) return names
  return names.filter((n) => n.toLowerCase().includes(needle))
}

export function TodosAssigneeCombobox({
  facet,
  person,
  people,
  onChange,
  lens = 'org',
  testId = testIds.basecamp.todosAssigneeFacet,
}: {
  facet: AssigneeFacet
  person: string | null
  people: GroupedAssigneeNames
  onChange: (facet: AssigneeFacet, person: string | null) => void
  lens?: TodosListMode
  testId?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const grouped = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return {
      clients: filterGroup(people.clients, needle),
      hop: filterGroup(people.hop, needle),
      unknown: filterGroup(people.unknown, needle),
    }
  }, [people, query])
  const hasNames = grouped.clients.length + grouped.hop.length + grouped.unknown.length > 0

  const kinds = lens === 'me' ? ME_KINDS : ORG_KINDS
  const label =
    facet === 'person' && person
      ? person
      : kinds.find((k) => k.id === facet)?.label ?? 'Everyone'

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setQuery('')
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 max-w-[200px] justify-between gap-1.5 text-xs font-normal"
          data-testid={testId}
          title="Assignees on this report, grouped Hop vs client. Not a Basecamp directory."
        >
          <User2 className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{label}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={4}
        alignOffset={0}
        className={cn(QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS, 'w-56 p-0')}
        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuLabel className="px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
          On this report
        </DropdownMenuLabel>
        {kinds.map((kind) => (
          <DropdownMenuItem
            key={kind.id}
            className="gap-2 text-xs"
            onSelect={() => onChange(kind.id, null)}
          >
            <Check
              className={cn(
                'h-3.5 w-3.5',
                facet === kind.id && !person ? 'opacity-100' : 'opacity-0',
              )}
            />
            {kind.label}
          </DropdownMenuItem>
        ))}
        <div
          className="flex items-center gap-2 border-t border-border px-2 py-1.5"
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search people..."
            className="h-7 min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground/50"
            autoFocus
          />
        </div>
        <div className="max-h-56 overflow-y-auto p-1">
          {!hasNames ? (
            <p className="px-2 py-3 text-xs text-muted-foreground">No matching people</p>
          ) : (
            <>
              {grouped.clients.length > 0 ? (
                <>
                  <DropdownMenuLabel className="px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
                    Client
                  </DropdownMenuLabel>
                  {grouped.clients.map((name) => (
                    <PersonRow
                      key={`client-${name}`}
                      name={name}
                      selected={facet === 'person' && person === name}
                      onSelect={() => onChange('person', name)}
                    />
                  ))}
                </>
              ) : null}
              {grouped.hop.length > 0 ? (
                <>
                  <DropdownMenuLabel className="px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
                    Hop Studios
                  </DropdownMenuLabel>
                  {grouped.hop.map((name) => (
                    <PersonRow
                      key={`hop-${name}`}
                      name={name}
                      selected={facet === 'person' && person === name}
                      onSelect={() => onChange('person', name)}
                    />
                  ))}
                </>
              ) : null}
              {grouped.unknown.length > 0 ? (
                <>
                  <DropdownMenuLabel className="px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
                    Other
                  </DropdownMenuLabel>
                  {grouped.unknown.map((name) => (
                    <PersonRow
                      key={`other-${name}`}
                      name={name}
                      selected={facet === 'person' && person === name}
                      onSelect={() => onChange('person', name)}
                    />
                  ))}
                </>
              ) : null}
            </>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
